import React from "react";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { listLeagues } from "../graphql/queries";
import { Trophy } from "lucide-react";
import {
  getCurrentLeague,
  getLeagueStatus,
  sortLeaguesByRelevance,
  League,
} from "../lib/leagueUtils";
import {
  getLeagueGame,
  getLeagueGameLabel,
  LeagueGame,
} from "../lib/leagueGames";

const client = generateClient();

interface LeagueSelectorProps {
  selectedLeagueId: string | null;
  onLeagueChange: (leagueId: string | null) => void;
  className?: string;
  showLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autoSelectCurrent?: boolean; // Whether to auto-select the current league if no selection
  game?: LeagueGame;
  view?: "all" | "active" | "past";
}

const LeagueSelector: React.FC<LeagueSelectorProps> = ({
  selectedLeagueId,
  onLeagueChange,
  className = "",
  showLabel = true,
  placeholder = "Select a league",
  disabled = false,
  autoSelectCurrent = false,
  game,
  view = "all",
}) => {
  // Fetch all leagues
  const { data: leagues, isLoading } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });

  const gameLeagues = leagues
    ? (leagues as League[]).filter(
        (league) => {
          const status = getLeagueStatus(league).status;
          return (
            (!game || getLeagueGame(league.game) === game) &&
            (view === "all" ||
              (view === "active" && status === "running") ||
              (view === "past" &&
                ["ended", "inactive"].includes(status)))
          );
        }
      )
    : [];

  // Sort leagues by relevance and get current league
  const sortedLeagues = gameLeagues.length
    ? sortLeaguesByRelevance(gameLeagues, selectedLeagueId || undefined)
    : [];
  const currentLeague = getCurrentLeague(gameLeagues);
  const selectedLeague = gameLeagues.find(
    (league: any) => league.id === selectedLeagueId
  );

  // Auto-select current league if no selection and autoSelectCurrent is true
  React.useEffect(() => {
    if (
      autoSelectCurrent &&
      !selectedLeagueId &&
      currentLeague &&
      leagues &&
      leagues.length > 0
    ) {
      onLeagueChange(currentLeague.id);
    }
  }, [
    autoSelectCurrent,
    selectedLeagueId,
    currentLeague,
    leagues,
    onLeagueChange,
  ]);

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        {showLabel && (
          <Label className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            League
          </Label>
        )}
        <Select disabled>
          <SelectTrigger className={className}>
            <SelectValue placeholder="Loading leagues..." />
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  if (gameLeagues.length === 0) {
    return (
      <div className={`space-y-2 ${className}`}>
        {showLabel && (
          <Label className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            League
          </Label>
        )}
        <Select disabled>
          <SelectTrigger className={className}>
            <SelectValue placeholder="No leagues available" />
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {showLabel && (
        <Label className="flex items-center gap-2">
          <Trophy className="h-4 w-4" />
          League
        </Label>
      )}
      <Select
        value={selectedLeagueId ?? ""}
        onValueChange={(value) => onLeagueChange(value || null)}
        disabled={disabled}
      >
        <SelectTrigger className={className}>
          <SelectValue placeholder={placeholder}>
            {selectedLeague ? (
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                <span>{selectedLeague.name}</span>
                <span className="text-muted-foreground text-sm">
                  ({getLeagueGameLabel(selectedLeague.game)} ·{" "}
                  {selectedLeague.format})
                </span>
              </div>
            ) : (
              placeholder
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {sortedLeagues.map((league: any) => {
            const status = getLeagueStatus(league);
            const isCurrent = currentLeague?.id === league.id;

            return (
              <SelectItem key={league.id} value={league.id}>
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{league.name}</span>
                      {isCurrent && (
                        <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {getLeagueGameLabel(league.game)} · {league.format}
                      <span
                        className={`ml-2 ${
                          status.status === "running"
                            ? "text-green-600"
                            : status.status === "upcoming"
                              ? "text-blue-600"
                              : status.status === "ended"
                                ? "text-orange-600"
                                : "text-gray-500"
                        }`}
                      >
                        • {status.message}
                      </span>
                    </div>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LeagueSelector;
